import { AgentRouting, VoiceAiProvider } from '@pypestream/api-services/urql';
import {
  Pype,
  PypeBeta,
  PypeConfig,
  PypeStyle,
} from '../xstate/prostudio.api-service.types';
import { decodeFromBase64 } from './encode-base64';
// The one canonical spelling of the connector's name. `platform-catalog` reaches
// only `capitalize-words`, so this import keeps the module free of anything heavy.
import { CONTACT_CENTER_INTEGRATION_NAME } from './platform-catalog';
import { toIntegrationConfig, toRoutingConfig } from './pype-agent-routing';

/**
 * What a Genesys voice channel stores in `metadata_integration_info_b64`: OAuth
 * credentials, typed into the voice channel form. `integrator` is load-bearing
 * — Engagement's Genesys paths (`maybe_start_session_enrichment`,
 * `genesys_handoff_configured?`) branch on it being exactly `"Genesys"`.
 *
 * Required, because this is the shape Pro Studio WRITES. What comes back off
 * the wire may be partial — see `LiveAgentConfig`.
 */
export interface GenesysLiveAgentConfig {
  integrator: string;
  client_id: string;
  client_secret: string;
  grant_type: string;
  instance_url: string;
  o_auth_url: string;
}

/**
 * What a Contact Center voice channel stores in the same slot: the agent-assist
 * account and voice inbox, and nothing else. Engagement reads these two when a
 * `voice_inbox` handoff fires (`id_value/3`), accepting `inbox_id` as an alias
 * for `voice_inbox_id`.
 */
export interface ContactCenterLiveAgentConfig {
  integrator: string;
  account_id: string;
  inbox_id: string;
  /**
   * Read-only alias for `inbox_id`. Engagement looks up
   * `["voice_inbox_id", "inbox_id"]`, and the earlier contract named the first,
   * so a blob written against it may carry that key instead. Pro Studio always
   * WRITES `inbox_id`.
   */
  voice_inbox_id?: string;
}

/**
 * The `integrator` a Genesys blob carries, and the Platform picker's option
 * value for it. One spelling, because Engagement matches it exactly.
 */
export const GENESYS_INTEGRATOR_NAME = 'Genesys';

/**
 * The decoded `metadata_integration_info_b64` — ONE slot, two platforms.
 *
 * A UNION of the per-platform shapes rather than a single interface with every
 * field optional. That difference is the point: all-optional says
 * `client_secret` and `inbox_id` can occur together, which is exactly what
 * cannot happen (`buildVoiceAiConfig` REPLACES the blob rather than merging),
 * and it loses the fact that Genesys's six fields travel together. A union
 * also stops a reader reaching a field before deciding which platform it has —
 * use `isGenesysLiveAgentConfig` / `isContactCenterLiveAgentConfig` to narrow.
 *
 * `Partial` on each arm because this is `JSON.parse` of stored content: a
 * half-written blob is representable on the wire, so it has to be representable
 * here. The required forms above remain the contract for what we WRITE.
 */
export type LiveAgentConfig =
  | Partial<GenesysLiveAgentConfig>
  | Partial<ContactCenterLiveAgentConfig>;

/**
 * Fold an `integrator` for comparison. Separators and case both go: it is
 * hand-typed on the platform side and has reached storage spelled
 * `ContactCenter`, `contact_center` and `Contact Center`.
 *
 * Deliberately not shared with `contact-center-voice`'s fold of the same shape:
 * that one reads a CONNECTION's `platformId`, a different field answering a
 * different question, and tightening one for the other's sake is how a shared
 * predicate silently moves a second contract.
 */
const foldIntegrator = (value?: string | null): string =>
  (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

/** The blob's own claim about which platform it belongs to, folded. */
const integratorOf = (config?: LiveAgentConfig | null): string =>
  foldIntegrator((config as { integrator?: string } | null)?.integrator);

/** Engagement reads `["voice_inbox_id", "inbox_id"]`, so a stored blob may use either. */
const inboxIdOf = (
  config?: Partial<ContactCenterLiveAgentConfig> | null
): string => (config?.inbox_id ?? config?.voice_inbox_id ?? '').trim();

/**
 * Which platform a stored blob belongs to.
 *
 * **`integrator` decides when it is there.** The blob states its own platform,
 * so nothing is inferred from which keys happen to be present, and the two
 * guards are mutually exclusive by construction.
 *
 * **A blob with no `integrator` but BOTH ids is Contact Center.** That is not a
 * guess: the only writer of such a blob is the hand/API configuration CC-4070
 * exists to replace, and both ids together name an inbox unambiguously. Without
 * this, those channels open on None and the next save clears them — the very
 * data loss the ticket is about. One id alone is not enough; it names no inbox.
 *
 * The same leniency is deliberately NOT extended to Genesys: its blob has always
 * carried `integrator` (`buildVoiceAiConfig` writes `integrator: laPlatform`),
 * so a credential-only blob is a half-finished edit rather than a known legacy
 * shape, and treating it as Genesys would put another platform's data in those
 * fields.
 */
export const isContactCenterLiveAgentConfig = (
  config?: LiveAgentConfig | null
): config is Partial<ContactCenterLiveAgentConfig> => {
  const integrator = integratorOf(config);
  if (integrator) {
    return integrator === foldIntegrator(CONTACT_CENTER_INTEGRATION_NAME);
  }
  const candidate = config as Partial<ContactCenterLiveAgentConfig> | null;
  return !!(candidate?.account_id ?? '').trim() && !!inboxIdOf(candidate);
};

export const isGenesysLiveAgentConfig = (
  config?: LiveAgentConfig | null
): config is Partial<GenesysLiveAgentConfig> =>
  integratorOf(config) === foldIntegrator(GENESYS_INTEGRATOR_NAME);

export interface HandoffOptions {
  [skillTag: string]: {
    destination: string;
    type: string;
  };
}

export interface Channel {
  id: string;
  type?: 'widget' | 'twilio_voice' | 'vaapi';
  parent: string;
  env: 'live' | 'sandbox';
  streamId: string;
  pypeId: string;
  /** Bot the stream starts chat with — maps to a solution via botSolutionId. */
  botId?: string;
  logo: string;
  agentRouting: PypeConfig['agent_routing'];
  name: string;
  config?: PypeConfig['config'];
  /** Callback URL of the bound platform, part of its identity. */
  agentRoutingUrl?: string;
  /**
   * The pype's `agent_routing_config` verbatim — the bound connector's config as
   * stored. Kept raw so a save round-trips platform-specific credentials instead
   * of flattening them into `config.integration_headers`.
   */
  agentRoutingConfig?: Record<string, unknown> | null;
  botRetry?: PypeConfig['bot_retry'];
  style?: PypeStyle;
  beta?: PypeBeta;
  lastEdited?: string;
  customPypeCss?: string;
  analyticsEnabled?: boolean | null;
  /**
   * Contact Center inbox kind behind a `vaapi` channel — `vaapi_config.channel_type`
   * (`Channel::SMS`, `Channel::Whatsapp`, …). Only this is lifted from the config:
   * the rest of it holds the agent bot token.
   */
  vaapiChannelType?: string;
  twilioVoiceConfig?: {
    account_sid: string;
    auth_token: string;
    handoff_url: string;
    init_call_webhook_url: string;
    language: string;
    phone_number: string;
    speech_model: string;
    transcription_provider: string;
    tts_provider: string;
    voice: string;
    welcome_greeting: string;
    elevenlabs_audio_model: string;
    elevenlabs_voice_id: string;
    elevenlabs_similarity: number;
    elevenlabs_speed: number;
    elevenlabs_stability: number;
    elevenlabs_voice_metadata_b64?: string;
    elevenlabs_voice_metadata?: VoiceAiProvider;
    metadata_integration_info_b64?: string;
    live_agent_config?: LiveAgentConfig;
    voice_handoff_options_b64?: string;
    /** Multi-language overlay (PSTUDIO-2950); decoded by languagesFromTwilioConfig. */
    languages_config_b64?: string | null;
    handoff_options?: HandoffOptions;
    user_silence_timeout?: number | null;
    inbound_message_buffer_ms?: number | null;
    eot_threshold?: number | null;
    prompt_max_retries?: number | null;
    retry_prompt_prefix?: string | null;
    /**
     * Stored by the Channels API as a boolean OR as the strings 'true' /
     * 'undefined' / 'null' — read it through `isCallRecordingEnabled`, never
     * by truthiness.
     */
    enable_call_recording?: boolean | string | null;
  };
}

/** ISO string for a timestamp, or '' when it is absent or unparseable. */
const toIsoDate = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const VAAPI_CHANNEL_LABELS: Record<string, string> = {
  'Channel::SMS': 'SMS',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::Telegram': 'Telegram',
  'Channel::WebWidget': 'Web Widget',
};

/** Display label for a Contact Center (VAAPI) channel's `channel_type`. */
export const getVaapiChannelLabel = (channelType?: string): string =>
  (channelType && VAAPI_CHANNEL_LABELS[channelType]) || 'Contact Center';

/**
 * `channel_type` from a stream's `vaapi_config`. Typed as a String in the schema,
 * but the action passes the Pypes API body through (as with
 * `twilio_voice_config`), so accept either a JSON string or an object.
 */
const readVaapiChannelType = (config: unknown): string | undefined => {
  let parsed = config;
  if (typeof config === 'string') {
    try {
      parsed = JSON.parse(config);
    } catch {
      return undefined;
    }
  }
  if (!parsed || typeof parsed !== 'object') return undefined;
  const channelType = (parsed as { channel_type?: unknown }).channel_type;
  return typeof channelType === 'string' && channelType
    ? channelType
    : undefined;
};

export const getChannelsFromPypes = (pypes: Pype[]): Channel[] => {
  return (
    pypes?.flatMap(
      (pype) =>
        pype.streams?.flatMap((stream): Channel[] => {
          if (stream.twilio_voice_enabled) {
            if (!stream.id) {
              console.warn(
                '[getChannelsFromPypes] Skipping twilio_voice channel: stream.id is empty',
                { pypeId: pype.id, streamName: stream.name }
              );
              return [];
            }
            return [
              {
                id: stream.id,
                type: 'twilio_voice' as const,
                streamId: stream.id || '',
                parent: pype.name || '',
                env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                pypeId: pype.id || '',
                botId: stream.start_chat_bot_id || undefined,
                logo: pype.logo || '',
                agentRouting: pype.agent_routing as AgentRouting,
                name: stream.name || '',
                twilioVoiceConfig: (() => {
                  const config =
                    stream.twilio_voice_config as Channel['twilioVoiceConfig'];
                  if (!config) return config;

                  let decoded: Channel['twilioVoiceConfig'] = { ...config };

                  if (
                    config.elevenlabs_voice_metadata_b64 &&
                    config.elevenlabs_voice_metadata_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        elevenlabs_voice_metadata: JSON.parse(
                          decodeFromBase64(config.elevenlabs_voice_metadata_b64)
                        ),
                      };
                    } catch (error) {
                      console.error(
                        'Failed to decode elevenlabs voice metadata:',
                        error
                      );
                    }
                  }

                  if (
                    config.metadata_integration_info_b64 &&
                    config.metadata_integration_info_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        live_agent_config: JSON.parse(
                          decodeFromBase64(config.metadata_integration_info_b64)
                        ),
                      };
                    } catch (error) {
                      console.error(
                        'Failed to decode live agent config:',
                        error
                      );
                    }
                  }

                  if (
                    config.voice_handoff_options_b64 &&
                    config.voice_handoff_options_b64 !== 'null'
                  ) {
                    try {
                      decoded = {
                        ...decoded,
                        handoff_options: JSON.parse(
                          decodeFromBase64(config.voice_handoff_options_b64)
                        ),
                      };
                    } catch (error) {
                      console.error('Failed to decode handoff options:', error);
                    }
                  }

                  return decoded;
                })(),
                lastEdited: stream.last_updated
                  ? toIsoDate(stream.last_updated)
                  : '',
                analyticsEnabled: stream.analytics_enabled,
              },
            ];
          }
          const vaapiChannels: Channel[] = [];
          if (stream.vaapi_enabled) {
            if (stream.id) {
              vaapiChannels.push({
                id: stream.id,
                type: 'vaapi',
                streamId: stream.id,
                parent: pype.name || '',
                env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                pypeId: pype.id || '',
                botId: stream.start_chat_bot_id || undefined,
                logo: pype.logo || '',
                agentRouting: pype.agent_routing as AgentRouting,
                config: toIntegrationConfig(pype.agent_routing_config),
                agentRoutingUrl: pype.agent_routing_url ?? undefined,
                agentRoutingConfig: toRoutingConfig(pype.agent_routing_config),
                botRetry: pype.bot_retry,
                name: stream.name || '',
                vaapiChannelType: readVaapiChannelType(stream.vaapi_config),
                lastEdited: toIsoDate(stream.last_updated),
                analyticsEnabled: stream.analytics_enabled,
              });
            } else {
              console.warn(
                '[getChannelsFromPypes] Skipping vaapi channel: stream.id is empty',
                { pypeId: pype.id, streamName: stream.name }
              );
            }
          }
          // A Contact Center stream can also carry Pype widgets; those keep
          // their own rows (they have their own ids and settings pages).
          return vaapiChannels.concat(
            stream.widgets?.flatMap((widget): Channel[] => {
              if (!widget.id) {
                console.warn(
                  '[getChannelsFromPypes] Skipping widget channel: widget.id is empty',
                  {
                    pypeId: pype.id,
                    streamId: stream.id,
                    widgetName: widget.widget_name,
                  }
                );
                return [];
              }
              return [
                {
                  type: 'widget',
                  parent: pype.name || '',
                  env: pype.env?.toLowerCase() as 'live' | 'sandbox',
                  streamId: stream.id || '',
                  pypeId: pype.id || '',
                  botId: stream.start_chat_bot_id || undefined,
                  logo: pype.logo || '',
                  agentRouting: pype.agent_routing as AgentRouting,
                  config: toIntegrationConfig(pype.agent_routing_config),
                  agentRoutingUrl: pype.agent_routing_url ?? undefined,
                  agentRoutingConfig: toRoutingConfig(
                    pype.agent_routing_config
                  ),
                  botRetry: pype.bot_retry,
                  id: widget.id,
                  name: widget.widget_name || '',
                  // `new Date('').toISOString()` throws RangeError, so an
                  // unset/invalid last_updated would take down the whole channel
                  // list rather than just this pill (the twilio branch below was
                  // already guarded).
                  lastEdited: toIsoDate(widget.last_updated),
                  style: widget.style ? JSON.parse(widget.style) : undefined,
                  beta: widget.beta ? JSON.parse(widget.beta) : undefined,
                  customPypeCss: widget.custom_pype_css || '',
                  analyticsEnabled: stream.analytics_enabled,
                },
              ];
            }) || []
          );
        }) || []
    ) || []
  );
};
